"use client";

import React, { useRef, useState, MouseEvent } from "react";
import { Book, Play, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const features = [{
  title: "Documentation",
  description: "Explore our comprehensive guides and API references to master the Webshingle platform and build your site with confidence.",
  icon: Book
}, {
  title: "Video Tutorials",
  description: "Watch step-by-step visual walkthroughs that show you exactly how to go from a simple prompt to a fully functional WordPress site.",
  icon: Play
}, {
  title: "Community Forum",
  description: "Join a thriving network of creators and business owners to share tips, ask questions, and grow your digital presence together.",
  icon: Users
}];
function TiltCard({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / centerY * -15;
    const rotateY = (x - centerX) / centerX * 15;
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const Icon = feature.icon;
  return <div data-index={index} className="relative group h-full w-full" style={{
    perspective: "1000px"
  }}>
      {}
      <div ref={cardRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter} className="h-full w-full rounded-2xl bg-border transition-all duration-200 ease-out will-change-transform group-hover:bg-primary">
        <Card className="relative h-full w-full overflow-hidden border-none bg-white transition-colors duration-500">
          {}
          <div className={`absolute inset-0 bg-primary/5 opacity-0 transition-opacity duration-500 ${isHovered ? "opacity-100" : ""}`} />

          <CardHeader className="relative z-10 pb-4 pt-10">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-xl bg-muted shadow-md ring-1 ring-border transition-transform duration-500 group-hover:scale-110 group-hover:bg-primary/10 group-hover:ring-primary/30">
              <Icon className="h-8 w-8 text-secondary group-hover:text-primary" strokeWidth={1.5} />
            </div>
            <CardTitle className="font-default text-2xl font-bold tracking-tight text-secondary">
              {feature.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10 pb-10">
            <CardDescription className="font-default text-base leading-relaxed text-muted-foreground">
              {feature.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function WhyThisIntensive() {
  return <section data-section-id="222770" id="quick-resources" className="relative overflow-hidden bg-white py-32 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-1/4 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-primary/5 mix-blend-multiply blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 h-[600px] w-[600px] translate-x-1/3 translate-y-1/3 rounded-lg bg-primary/5 mix-blend-multiply blur-[150px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-20 text-center">
          <h2 className="font-default text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl text-secondary">
            Quick{" "}
            <span className="text-primary">
              Resources
            </span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl font-default text-lg text-muted-foreground">
            Everything you need to launch, manage, and scale your professional WordPress website with Webshingle's AI-powered tools.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {features.map((feature, index) => <TiltCard key={index} feature={feature} index={index} />)}
        </div>
      </div>
    </section>;
}